/**
 * SpecialDocs.tsx — lista dokumentów specjalnych tenanta (klasa 'specjalne'); kolumny
 * DATA-DRIVEN z katalogu (mirror `CommercialDocs.tsx`, 7. i ostatnia klasa dokumentów).
 *
 * Moduł: crm_core (dokumenty w pakiecie bazowym, ADR-0083).
 * Backend: GET /api/special-docs (widoczność liczy silnik uprawnień per firma × reguła — firma,
 * typ dokumentu, etap obiegu, warunki — w granicach firm, do których user ma dostęp).
 * Klasa 'specjalne' NIE ma stron (brak counterparty cache) i brak satelity.
 *
 * Konfiguracja kolumn (nadpisania, wykluczenia, slugi `?columns=`, mapa id→slug, typ wiersza) żyje
 * w `lib/documents/standard-document-columns` — wspólna z listami dokumentów handlowych i
 * wewnętrznych oraz z sekcją kokpitu decyzji; kolumny procesu buduje wspólne
 * `lib/documents/process-columns`.
 *
 * Kolumny rdzenne buduje `buildCoreColumns` z katalogu pól (`GET /system-modules/specjalne/
 * fields`): pole z „Pokaż na liście" (showInList) + typ renderowalny = kolumna; klucz w wierszu
 * odpowiedzi wyliczany mechanicznie ze sluga (camelCase) — ZERO mapy slug→klucz per strona.
 * Numer systemowy (mono), tytuł (pogrubiony, nieukrywalny), status i pilność (lista wyboru w
 * wierszu filtrów) mają bogaty render przez rejestr nadpisań (`STANDARD_DOC_OVERRIDES`); data
 * dokumentu renderuje się generykiem.
 * Firma/opiekun/źródło idą dedykowanymi budowniczymi (record-select / i18n label), więc są
 * WYKLUCZONE z `buildCoreColumns` (`excludeSlugs`); kategoria to też kolumna ręczna
 * (`buildCategoryColumn` — w katalogu `category_id` ma `showInList: false`). Kolumny procesu
 * (aktywny proces / bieżący etap) to pola WIRTUALNE silnika (poza katalogiem) — domyślnie
 * widoczne gdy moduł procesu aktywny. Pola dodatkowe (ext) przez `buildExtColumns`.
 *
 * Tylko ta lista ma: tryb kalendarza (domyślny układ), panel „Struktura organizacyjna" (filtr po
 * polu `employee`, w kontekście zawężenia do typu dokumentu), zawężenie do typów dokumentu
 * (`useDocumentTypeNarrowing`) i „Pokaż zarchiwizowane" w menu Akcje.
 *
 * Uwaga: slug systemu = 'specjalne', route = '/special-docs', api = '/special-docs'.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DOCUMENT_TYPE_FILTER_FIELD } from '@saas/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Network, Plus, RotateCcw, Star, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListViewModeToggle,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import { listLoadRetry } from '@/components/configurable-table/load-error';
import {
  type ListResponseMeta,
  useServerListControls,
} from '@/components/configurable-table/useServerListControls';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DocumentTypePickerModal } from '@/components/documents/DocumentTypePickerModal';
import { IconButton } from '@/components/IconButton';
import { ModuleGuard } from '@/components/ModuleGuard';
import { OrgTreeFilter } from '@/components/org-tree-filter/OrgTreeFilter';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useActiveMenuItem } from '@/hooks/useActiveMenuItem';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useReportRecordLock } from '@/hooks/useReportRecordLock';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import { buildDocumentCoreRefColumns } from '@/lib/documents/document-core-ref-columns';
import { buildProcessColumns } from '@/lib/documents/process-columns';
import {
  SPECIAL_PROCESS_LABEL_PREFIX,
  type SpecialDocRow,
  STANDARD_DOC_ALWAYS_SLUGS,
  STANDARD_DOC_COLUMN_ID_TO_SLUG,
  STANDARD_DOC_EXCLUDE_SLUGS,
  STANDARD_DOC_OVERRIDES,
} from '@/lib/documents/standard-document-columns';
import { useDocumentTypeNarrowing } from '@/lib/documents/use-document-type-narrowing';
import {
  buildCategoryColumn,
  buildCompanyColumn,
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListActions } from '@/lib/list-actions/useListActions';
import { listFilterSkipSlugs, useListFilterOptions } from '@/lib/list-filter-options';
import { baseFiltersToHeaderFilters } from '@/lib/menu/menu-item-view-runtime';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';

const SPECIAL_DOCS_KEY = 'special-docs';

// Pola rysowane nadpisaniem kolumny albo osobnym builderem nie pobierają słownika opcji filtra.
const FILTER_OPTIONS_SKIP_SLUGS = listFilterSkipSlugs(
  STANDARD_DOC_OVERRIDES,
  STANDARD_DOC_EXCLUDE_SLUGS,
);

/**
 * Zwinięcie panelu struktury organizacyjnej — preferencja WIDZA (per przeglądarka), nie dane
 * tenanta, więc localStorage, a nie układ listy. Każdy dostęp w try/catch: w trybie prywatnym
 * albo przy zablokowanych danych witryny odczyt/zapis rzuca, a wtedy panel ma po prostu
 * zachowywać się domyślnie, nie wywracać strony.
 */
const ORG_FILTER_HIDDEN_KEY = 'special-docs:org-tree-filter-hidden';

function readOrgFilterHidden(): boolean {
  try {
    return window.localStorage.getItem(ORG_FILTER_HIDDEN_KEY) === 'true';
  } catch {
    // Storage niedostępny — panel zostaje widoczny (stan tylko na tę sesję).
    return false;
  }
}

interface ListResponse {
  specialDocs: SpecialDocRow[];
  limit: number;
  offset: number;
  total: number;
  /** Slugi kolumn CORE sortowalnych serwerowo (silnik list) — patrz `useServerListControls`. */
  meta?: ListResponseMeta;
  /** Etykiety pól `reference` (sibling do wierszy) — patrz `ExtRefLabels`. */
  extRefLabels?: ExtRefLabels;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function SpecialDocs() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showTypePicker, setShowTypePicker] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [showDeleted, setShowDeleted] = useState(false);

  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  // Tryb widoku + tryby dostępne w bieżącym layoucie (raport tabeli) — wspólny hook, przełącznik
  // w nagłówku jak na pozostałych listach dokumentów. Domyślny układ tej listy otwiera się
  // w kalendarzu (`SPECIAL_DEFAULT_LIST_CONFIG.defaultViewMode`) — pastylka aktywna od wejścia.
  const viewModes = useListViewMode();
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  // Sort serwerowy + paginacja (silnik list) — wspólny hook, patrz useServerListControls.ts.
  const controls = useServerListControls();
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Zmiana filtrów w nagłówku wraca na pierwszą stronę — DWA callsite'y (ListFilterBar +
  // ConfigurableTable), więc zawinięte w jeden stabilny handler.
  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );

  const [confirmDelete, setConfirmDelete] = useState<SpecialDocRow | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<SpecialDocRow | null>(null);

  // Zaznaczenie wierszy (checkbox po lewej) → akcje na zaznaczonych z menu „Akcje" (PHASE 25).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Zbiór kolumn FAKTYCZNIE widocznych w tabeli teraz — zasilany przez
  // `onVisibleColumnsChange` (ConfigurableTable). `null` przed pierwszym wywołaniem
  // -> `visibleColumnParams` niżej spada na `listLayout`/`defaultVisible`.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);

  // Aktywna firma (CompanySwitcher) — listing zawężany po stronie backendu (silnik uprawnień:
  // widoczność per firma × reguła).
  // Tryb "Wszystkie firmy" → brak ?companyId= (backend zwraca unię po firmach usera).
  const activeCompanyScope = useActiveCompanyScope();
  const companyId = companyIdParam(activeCompanyScope);
  const { me } = useTenantAuth();
  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);
  // Moduł procesu aktywny → kolumny przekrojowe (aktywny proces / bieżący etap) domyślnie
  // widoczne (gdy brak układu). Nieaktywny → domyślnie ukryte — mirror `CommercialDocs.tsx`.
  const isProcessModuleActive = useMemo(
    () => me?.modules?.some((m) => m.code === 'processes') ?? false,
    [me?.modules],
  );

  // Pozycja menu (`?mi=<id>`) przez którą klient wszedł — narzuca filtry stałe („Warunki")
  // skonfigurowane w Kreatorze menu (np. status = zatwierdzony). Mirror `InternalDocs.tsx`.
  const activeMenuItem = useActiveMenuItem();
  const menuBaseFilters = activeMenuItem?.view?.baseFilters;

  // Zawężenie do wybranych typów dokumentu klasy 'specjalne' (np. moduł Urlopy montujący
  // listę wniosków zawężoną do typu WU) — unia trzech źródeł (URL `?documentTypeIds=`/legacy
  // `?documentTypeId=` + filtr stały pozycji menu), patrz `useDocumentTypeNarrowing`.
  const narrowedTypeIds = useDocumentTypeNarrowing();

  // Panel „Struktura organizacyjna" — drzewo działów z checkboxami, zawęża listę ORAZ kalendarz
  // po polu dodatkowym `employee` (jeden filtr nagłówka niżej, więc oba widoki jadą z tego
  // samego zapytania). Renderowany WYŁĄCZNIE w kontekście zawężenia do typu dokumentu (moduł
  // Urlopy) — ogólna lista dokumentów specjalnych nie ma pola `employee`.
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [orgFilterHidden, setOrgFilterHidden] = useState<boolean>(readOrgFilterHidden);
  const toggleOrgFilter = useCallback(() => {
    setOrgFilterHidden((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(ORG_FILTER_HIDDEN_KEY, String(next));
      } catch {
        // Storage niedostępny — preferencja działa tylko do przeładowania strony.
      }
      return next;
    });
  }, []);
  const showOrgFilter = narrowedTypeIds.length > 0 && !orgFilterHidden;

  // Zmiana zakresu firmy, zawężenia do typu dokumentu, wyboru pracowników lub filtrów stałych
  // pozycji menu („Warunki" — przejście do innej pozycji menu) zawęża wynik — reset strony jak
  // przy filtrach/sorcie (inaczej user może utknąć na pustej stronie N). Zaznaczenie czyścimy,
  // bo zawężony wynik może już nie zawierać zaznaczonych wierszy.
  useEffect(() => {
    controls.resetPage();
    setSelectedIds([]);
  }, [companyId, narrowedTypeIds, employeeIds, menuBaseFilters, controls.resetPage]);

  // Zmiana firmy lub zawężenia do typu dokumentu czyści wybór w panelu struktury — inaczej
  // wybrani pracownicy zostają "wiszący" (dociągają filtr `employee` do backendu) po przejściu
  // do innego kontekstu, w którym panel/pole `employee` już nie istnieje albo dotyczy innych
  // osób. CELOWO bez `employeeIds` w zależnościach (ten efekt pisze do tego stanu — pętla).
  // No-op przy pierwszym renderze jest nieszkodliwy (`employeeIds` startuje puste).
  useEffect(() => {
    setEmployeeIds([]);
  }, [companyId, narrowedTypeIds]);

  // Katalog pól modułu `specjalne` (core + ext) — źródło kolumn dodatkowych (`extColumns`).
  const fieldsQ = useSystemModuleFields('specjalne');
  // Listy wyboru w wierszu filtrów, które trzeba wczytać: pozycje słowników pól `select` (dokumenty
  // specjalne dziś nie mają słownikowych — status/pilność to enumy z kodu) i użytkownicy dla „Opiekuna".
  const filterOptions = useListFilterOptions(fieldsQ.data, FILTER_OPTIONS_SKIP_SLUGS);
  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`), aktualizowany
  // niżej PO `listQ`.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();
  const extColumns = useMemo<ColumnDefinition<SpecialDocRow>[]>(
    () => buildExtColumns<SpecialDocRow>(fieldsQ.data ?? [], t, getRefLabels),
    [fieldsQ.data, t, getRefLabels],
  );

  // Wymuszony layout listy z pozycji Kreatora menu, z której user przyszedł
  // (kartoteka-layout-per-menu) — ta sama zmienna `tableId` musi iść do WSZYSTKICH
  // konsumentów niżej (ListFilterBar, ConfigurableTable), inaczej widżety i tabela
  // renderują dwa różne layouty.
  const withMenuItem = useWithMenuItem();
  const forcedListLayoutId = useForcedListLayoutId('specjalne');
  const tableId = forcedListLayoutId ? `specjalne::layout-${forcedListLayoutId}` : 'specjalne';

  // Layout zapisany przez usera (Kreator Listy) — źródło aktualnej widoczności kolumn dla
  // `visibleColumnParams` niżej (ConfigurableTable czyta ten sam cache po `tableId`).
  const { layout: listLayout } = useTableLayout(tableId);

  // Kolumny rdzenne z katalogu (klucz mechaniczny ze sluga; garstka przez rejestr nadpisań).
  // Firma/opiekun/źródło wykluczone (dedykowane budowniczowie niżej); kategoria — `buildCategoryColumn`.
  const coreColumns = useMemo<ColumnDefinition<SpecialDocRow>[]>(
    () =>
      buildCoreColumns<SpecialDocRow>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels,
        overrides: STANDARD_DOC_OVERRIDES,
        excludeSlugs: STANDARD_DOC_EXCLUDE_SLUGS,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
        selectOptions: filterOptions.selectOptions,
      }),
    [
      fieldsQ.data,
      t,
      getRefLabels,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
      filterOptions.selectOptions,
    ],
  );

  // Kolumny przekrojowe silnika (poza katalogiem) — aktywny proces / bieżący etap. Domyślnie
  // widoczne gdy moduł procesu aktywny (inaczej byłyby kolumnami samych myślników) — mirror
  // `CommercialDocs.tsx`. Status wniosku/dokumentu pokazuje „Bieżący etap" (etap końcowy sprawy
  // zakończonej).
  const processColumns = useMemo<ColumnDefinition<SpecialDocRow>[]>(
    () =>
      buildProcessColumns<SpecialDocRow>({
        t,
        labelKeyPrefix: SPECIAL_PROCESS_LABEL_PREFIX,
        isSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        isProcessModuleActive,
      }),
    [t, controls.isCoreColumnSortable, sortableSlugs, isProcessModuleActive],
  );

  const columns = useMemo<ColumnDefinition<SpecialDocRow>[]>(
    () => [
      ...coreColumns,
      buildCompanyColumn<SpecialDocRow>({ companies, getRefLabels, t }),
      // Kategoria — wspólny builder (`@/lib/ext-list-columns`), JEDNO źródło prawdy dla
      // Kontrahentów (pilot) + 7 klas dokumentów.
      buildCategoryColumn<SpecialDocRow>({
        t,
        resolveSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
      }),
      // Opiekun / źródło — wspólny builder (`@/lib/documents/document-core-ref-columns`).
      // `withSource: true` — klasa 'specjalne' MA kolumnę źródła na liście, mirror commercial.
      ...buildDocumentCoreRefColumns<SpecialDocRow>({
        t,
        getRefLabels,
        withSource: true,
        resolveSourceSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        resolveSourceFilterable: (slug) => controls.isCoreColumnFilterable(slug, filterableSlugs),
        // Filtr opiekuna: lista użytkowników (`record-select`), gdy serwer go serwuje i lista się wczytała.
        resolveOwnerFilterable: (slug) => controls.isCoreColumnFilterable(slug, filterableSlugs),
        ownerFilterOptions: filterOptions.ownerOptions,
      }),
      ...processColumns,
      ...extColumns,
    ],
    [
      coreColumns,
      companies,
      getRefLabels,
      t,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
      filterOptions.ownerOptions,
      processColumns,
      extColumns,
    ],
  );

  // Mapa id kolumny → slug backendu dla `?columns=`. id = slug wszędzie poza dedykowanymi
  // budowniczymi spoza katalogu (firma ma id `company`, reszta id = slug 1:1).
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = { ...STANDARD_DOC_COLUMN_ID_TO_SLUG };
    for (const c of coreColumns) map[c.id] = c.id; // id = slug (buildCoreColumns)
    return map;
  }, [coreColumns]);

  // Filtry do backendu jako pojedyncza tablica `headerFilters`:
  // (1) popup w nagłówku (mode=values/condition/range) +
  // (2) input „Szukaj…" pod headerem (konwersja na mode=condition contains).
  const serializedHeaderFilters = useMemo(() => {
    const merged: ColumnHeaderFilter[] = [];

    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }

    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }

    // Wybór w panelu struktury organizacyjnej — filtr „wartości" po polu dodatkowym `employee`
    // (mirror kontrolki „Pracownik" z listy dokumentów wewnętrznych). Kanał w pełni generyczny
    // po stronie API (filtr wartości silnika list na kolumnie ext), więc nie wymaga backendu.
    // Bramka `narrowedTypeIds.length > 0` — pole `employee` istnieje TYLKO w kontekście zawężenia
    // do typu dokumentu (moduł Urlopy); ogólna lista dokumentów specjalnych go nie ma. CELOWO
    // BEZ `orgFilterHidden` — schowanie panelu to preferencja widoku, wybór ma zostać aktywny.
    if (narrowedTypeIds.length > 0 && employeeIds.length > 0) {
      merged.push({ field: 'employee', mode: 'values', selectedValues: employeeIds });
    }

    // Filtry stałe z pozycji menu (`?mi=`) — dokładane WYŁĄCZNIE tutaj (poza reactowym
    // stanem headerFilters), więc są niewidoczne i nieusuwalne dla usera; łączone AND
    // z jego własnymi filtrami (mirror `InternalDocs.tsx`). Filtr typu dokumentu
    // (`DOCUMENT_TYPE_FILTER_FIELD`) jest WYCIĘTY stąd — silnik list go nie wystawia jako
    // ogólną kolumnę (patrz `extractDocumentTypeIdsFromBaseFilters`), podróżuje wyłącznie
    // przez `?documentTypeIds=` (`narrowedTypeIds`), nie przez `headerFilters`.
    merged.push(
      ...baseFiltersToHeaderFilters(
        (menuBaseFilters ?? []).filter((f) => f.field !== DOCUMENT_TYPE_FILTER_FIELD),
        me?.user.id,
      ),
    );

    // Widok kalendarza dociąga tylko rekordy z wyświetlanego miesiąca (wspólny filtr z silnika).
    if (controls.calendarHeaderFilter) merged.push(controls.calendarHeaderFilter);

    return merged.length > 0 ? JSON.stringify(merged) : undefined;
  }, [
    headerFilters,
    debouncedColumnFilters,
    columns,
    narrowedTypeIds,
    employeeIds,
    menuBaseFilters,
    me?.user.id,
    controls.calendarHeaderFilter,
  ]);

  // Kolumny formatowania warunkowego (Kreator Listy) mogą celować w kolumnę aktualnie
  // NIEwidoczną (schowaną, ale z aktywną regułą) — `extraColumnIds` dopisuje jej slug do
  // `?columns=`, żeby reguła miała dane do ewaluacji nawet gdy kolumna sama nie jest wyświetlana.
  const formattingRuleColumnIds = useMemo(
    () => listLayout?.layoutConfig?.formattingRules?.map((r) => r.columnId) ?? [],
    [listLayout?.layoutConfig?.formattingRules],
  );

  // Kolumny aktualnie widoczne, jako slugi backendu (wspólny helper) — do zbudowania
  // `?columns=`. `gcrecord` w `alwaysSlugs` — czyta go akcja wiersza (restore/delete),
  // nie jest kolumną.
  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns:
          visibleColumnIds !== null
            ? columns.map((c) => ({ id: c.id, visible: visibleColumnIds.includes(c.id) }))
            : listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet: new Set(extColumns.map((c) => c.id)),
        alwaysSlugs: STANDARD_DOC_ALWAYS_SLUGS,
        extraColumnIds: formattingRuleColumnIds,
        // Pola kalendarza układu zawsze w `?columns=` — kalendarz nie zależy od widocznych kolumn.
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      visibleColumnIds,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extColumns,
      formattingRuleColumnIds,
    ],
  );

  const queryKey = [
    SPECIAL_DOCS_KEY,
    {
      q: debouncedSearch,
      include_deleted: showDeleted,
      companyId: companyId ?? 'all',
      documentTypeIds: narrowedTypeIds.length > 0 ? narrowedTypeIds : undefined,
      headerFilters: serializedHeaderFilters,
      columns: visibleColumnParams,
      ...controls.queryParams,
    },
  ];

  const listQ = useQuery({
    queryKey,
    // Trzyma poprzednie dane na ekranie podczas refetchu wywołanego zmianą klucza — bez
    // tego lista miga loaderem 2-3 razy przy każdym wejściu.
    placeholderData: keepPreviousData,
    // Awaria → jedno automatyczne ponowienie; zły adres / brak uprawnień / nieaktywny moduł →
    // bez ponawiania (komunikat od razu).
    retry: listLoadRetry,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (showDeleted) params.set('include_deleted', 'true');
      if (companyId) params.set('companyId', companyId);
      if (narrowedTypeIds.length > 0) params.set('documentTypeIds', narrowedTypeIds.join(','));
      if (serializedHeaderFilters) params.set('headerFilters', serializedHeaderFilters);
      if (visibleColumnParams.length > 0) {
        params.set('columns', visibleColumnParams.join(','));
      }
      // Sort + paginacja serwerowa (silnik list) — `sort` pominięty gdy brak (spada na
      // domyślny sort backendu), `order`/`limit`/`offset` zawsze wysyłane.
      if (controls.queryParams.sort) params.set('sort', controls.queryParams.sort);
      params.set('order', controls.queryParams.order);
      params.set('limit', String(controls.queryParams.limit));
      params.set('offset', String(controls.queryParams.offset));
      const res = await api.get<ListResponse>(`/special-docs?${params.toString()}`);
      return res.data;
    },
  });
  setRefLabels(listQ.data?.extRefLabels);
  // `meta.sortable` w stan przez EFEKT, nie w renderze (to useState → wywołanie w renderze pętli).
  useEffect(() => {
    setSortableSlugs(listQ.data?.meta?.sortable);
  }, [listQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(listQ.data?.meta?.filterable);
  }, [listQ.data?.meta?.filterable]);

  // Usuwanie i przywracanie z listy to zwykły zapis dokumentu — na czas zadania przycisku SQL na nim serwer
  // odpowiada 409 `RECORD_LOCKED_BY_RUN`; zamiast ogólnego błędu pokazujemy, które zadanie blokuje dokument.
  const reportRecordLock = useReportRecordLock();

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/special-docs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPECIAL_DOCS_KEY] });
      toast.show({ variant: 'success', title: t('tenant.documents.special.toast.deleted') });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      toast.show({ variant: 'error', title: t('tenant.documents.special.toast.deleteError') });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/special-docs/${id}/restore`, {});
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: [SPECIAL_DOCS_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['special-doc', id] });
      toast.show({ variant: 'success', title: t('tenant.documents.special.toast.restored') });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      toast.show({ variant: 'error', title: t('tenant.documents.special.toast.restoreError') });
    },
  });

  // Akcje listy (PHASE 25) — generyczny hook: resolver widoczności (katalog ∩ kartoteka ∩
  // moduły ∩ uprawnienia) + wykonawca (rozgłoszenie po ID na istniejące endpointy) + menu
  // kontekstowe (prawy klik) + potwierdzenia. Kartoteka `documents`, klasa `specjalne` → tylko
  // akcje uniwersalne. Eksport/import bez handlera → „wkrótce".
  // BEZ bramki „Wszystkie firmy": archiwizacja/przywróć biorą firmę z samego dokumentu (nie z
  // nagłówka X-Company-Id), więc działają też w trybie unii firm.
  const listActions = useListActions({
    kartotekaKey: 'documents',
    documentClass: 'specjalne',
    entity: 'special-docs',
    queryKey: [SPECIAL_DOCS_KEY],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    archivedView: showDeleted,
    // „Pokaż zarchiwizowane" żyje w menu Akcje (nie jako osobny przycisk na pasku listy) —
    // mirror `InternalDocs.tsx`.
    archivedToggle: {
      active: showDeleted,
      onToggle: () => {
        setShowDeleted((prev) => !prev);
        controls.resetPage();
        setSelectedIds([]);
      },
    },
    testIdPrefix: 'special-docs',
  });

  const docs = listQ.data?.specialDocs ?? [];

  return (
    <ModuleGuard moduleCode="crm_core">
      <main
        className="flex h-full min-h-0 flex-col bg-bg-secondary p-6"
        data-testid="special-docs-page"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-text-primary" data-testid="special-docs-title">
                {t('tenant.documents.special.title')}
              </h1>
              <p className="mt-1 text-sm text-text-tertiary">
                {t('tenant.documents.special.subtitle')}
              </p>
            </div>
            {/* Kolejność jak na pozostałych listach: widok → Akcje → Kreator → (przyciski
                dodatkowe tej listy, wzorem `Employees.tsx`) → Dodaj jako ostatni. */}
            <div className="flex flex-wrap items-center gap-2">
              <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="special-docs" />
              <ListActionsMenu {...listActions.menuProps} />
              <ListBuilderMenu slug="specjalne" testIdPrefix="special-docs" />
              {narrowedTypeIds.length > 0 && (
                <Button
                  variant="secondary"
                  onClick={toggleOrgFilter}
                  leftIcon={<Network size={14} />}
                  data-testid="special-docs-org-filter-toggle"
                >
                  {orgFilterHidden
                    ? t('tenant.orgTreeFilter.toggleShow')
                    : t('tenant.orgTreeFilter.toggleHide')}
                </Button>
              )}
              <Button
                onClick={() => setShowTypePicker(true)}
                leftIcon={<Plus size={14} />}
                data-testid="special-docs-add-btn"
              >
                {t('tenant.documents.special.actions.add')}
              </Button>
            </div>
          </header>

          {/* Panel struktury obok listy/kalendarza. Na wąskim ekranie (poniżej `md`) układ
              przełącza się na pionowy — panel ląduje NAD listą z własnym, ograniczonym scrollem,
              zamiast ściskać tabelę do nieczytelnej szerokości. */}
          <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
            {showOrgFilter && (
              <OrgTreeFilter
                companyId={companyId ?? null}
                selectedEmployeeIds={employeeIds}
                onChange={setEmployeeIds}
                className="max-h-64 w-full shrink-0 md:max-h-none md:w-72"
              />
            )}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {fieldsQ.isError ? (
                <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
              ) : (
                <ConfigurableTable<SpecialDocRow>
                  tableId={tableId}
                  testIdPrefix="special-docs"
                  showOrdinal
                  fillHeight
                  {...viewModes.tableProps}
                  /* Górny pasek listy WEWNĄTRZ karty tabeli (mirror `InternalDocs.tsx`):
                     wyszukiwarka + baton odśwież. „Pokaż zarchiwizowane" jest w menu Akcje. */
                  listToolbar={
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-[240px] flex-1">
                        <ListFilterBar
                          tableId={tableId}
                          columns={columns}
                          search={search}
                          onSearchChange={(next) => {
                            setSearch(next);
                            controls.resetPage();
                          }}
                          searchPlaceholder={t('tenant.documents.special.searchPlaceholder')}
                          headerFilters={headerFilters}
                          onHeaderFiltersChange={handleHeaderFiltersChange}
                          onRefresh={() =>
                            queryClient.invalidateQueries({ queryKey: [SPECIAL_DOCS_KEY] })
                          }
                          testIdPrefix="special-docs"
                        />
                      </div>
                    </div>
                  }
                  columns={columns}
                  data={docs}
                  isLoading={listQ.isPending}
                  getRowId={(row) => row.id}
                  headerFilters={headerFilters}
                  onHeaderFiltersChange={handleHeaderFiltersChange}
                  filterable
                  columnFilters={columnFilters}
                  onColumnFiltersChange={(next) => {
                    setColumnFilters(next);
                    controls.resetPage();
                  }}
                  onVisibleColumnsChange={setVisibleColumnIds}
                  selectable
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  onRowContextMenu={listActions.onRowContextMenu}
                  {...controls.tableProps}
                  totalItems={listQ.data?.total ?? 0}
                  isFetching={listQ.isFetching}
                  loadError={listQ.error}
                  onRetryLoad={() => void listQ.refetch()}
                  onCalendarRangeChange={controls.setCalendarRange}
                  onRowClick={(row) => navigate(withMenuItem(`/special-docs/${row.id}`))}
                  renderActions={(row) => (
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {row.gcrecord !== null ? (
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmRestore(row)}
                          disabled={restoreMutation.isPending}
                          aria-label={t('tenant.documents.special.actions.restore')}
                          data-testid={`special-docs-row-${row.id}-restore`}
                        >
                          <RotateCcw aria-hidden="true" />
                        </IconButton>
                      ) : (
                        <IconButton
                          variant="danger"
                          size="sm"
                          onClick={() => setConfirmDelete(row)}
                          disabled={deleteMutation.isPending}
                          aria-label={t('common.delete')}
                          data-testid={`special-docs-row-${row.id}-delete`}
                        >
                          <Trash2 aria-hidden="true" />
                        </IconButton>
                      )}
                    </div>
                  )}
                  actionsColumnLabel={t('tenant.documents.special.columns.actions')}
                  emptyIcon={Star}
                  emptyTitle={t('tenant.documents.special.empty')}
                />
              )}
            </div>
          </div>
        </div>

        <ConfirmDialog
          open={confirmDelete !== null}
          title={
            confirmDelete
              ? t('tenant.documents.special.deleteConfirm', {
                  number: confirmDelete.systemNumber ?? confirmDelete.title ?? confirmDelete.id,
                })
              : ''
          }
          message={t('tenant.documents.special.deleteConfirmBody')}
          confirmLabel={t('common.delete')}
          variant="danger"
          onConfirm={() => {
            if (confirmDelete) {
              deleteMutation.mutate(confirmDelete.id);
              setConfirmDelete(null);
            }
          }}
          onClose={() => setConfirmDelete(null)}
        />

        <ConfirmDialog
          open={confirmRestore !== null}
          title={
            confirmRestore
              ? t('tenant.documents.special.restoreConfirm', {
                  number: confirmRestore.systemNumber ?? confirmRestore.title ?? confirmRestore.id,
                })
              : ''
          }
          message={t('tenant.documents.special.restoreConfirmBody')}
          confirmLabel={t('tenant.documents.special.actions.restore')}
          variant="primary"
          onConfirm={() => {
            if (confirmRestore) {
              restoreMutation.mutate(confirmRestore.id);
              setConfirmRestore(null);
            }
          }}
          onClose={() => setConfirmRestore(null)}
        />
      </main>
      <DocumentTypePickerModal
        open={showTypePicker}
        docClass="specjalne"
        allowedTypeIds={narrowedTypeIds}
        onSelect={(typeId, pickedCompanyId) => {
          setShowTypePicker(false);
          navigate(
            withMenuItem(`/special-docs/new?documentTypeId=${typeId}&companyId=${pickedCompanyId}`),
          );
        }}
        onClose={() => setShowTypePicker(false)}
      />
      {listActions.overlays}
    </ModuleGuard>
  );
}
